import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { postOverlay, scopeBody, num, str, dayLabel } from '@/lib/overlayApi';
import type { DateRange } from '@/lib/dateRange';

export interface BrandOption {
  brand: string;
  sales: number;
  /** True when the brand belongs to the signed-in tenant. */
  mine: boolean;
}

/**
 * Brands competing in the current scope.
 *
 * Competitor market data is PUBLIC — OSA, distribution and estimated sales are
 * observed from the dark-store crawl — so the query is not tenant-filtered.
 * The tenant's own brands are flagged in the same pass (`is_mine`) purely to
 * mark them "(You)"; that used to take a second scoped request, and one scan
 * now answers both.
 */
export function useCompetitorBrands(
  customerName: string,
  category: string | undefined,
  range: DateRange,
  enabled: boolean,
): UseQueryResult<BrandOption[], Error> {
  return useQuery({
    queryKey: ['competitor-brands', customerName, category, range],
    enabled: enabled && Boolean(category),
    staleTime: 30 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const res = await postOverlay<Record<string, unknown>>(
        '/v1/availability/competition/brands',
        scopeBody({ customer_name: customerName, category }, range),
        signal,
      );
      return res.rows
        .map((r) => ({
          brand: str(r['brand']),
          sales: num(r['sales']) ?? 0,
          mine: r['is_mine'] === true,
        }))
        .filter((b) => b.brand.length > 0)
        // Own brands lead regardless of size. Ranking purely by sales buried
        // Scalpe Pro at #10 in Shampoos, so the one brand the user came to
        // compare sat inside the "+96" overflow while rivals held the chips.
        .sort((a, b) => (a.mine === b.mine ? b.sales - a.sales : a.mine ? -1 : 1));
    },
  });
}

export interface BrandSeries {
  labels: string[];
  byBrand: Record<string, Array<number | null>>;
}

/** Daily series of one metric, one line per selected brand. */
export function useBrandSeries(
  customerName: string,
  brands: string[],
  category: string | undefined,
  city: string | undefined,
  field: 'wt_osa' | 'listing',
  range: DateRange,
  enabled: boolean,
): UseQueryResult<BrandSeries, Error> {
  return useQuery({
    queryKey: ['brand-series', customerName, [...brands].sort(), category, city, range],
    enabled: enabled && brands.length > 0,
    staleTime: 10 * 60 * 1000,
    // The response carries both plottable fields, so switching the metric
    // re-reads the same cache entry instead of re-scanning.
    select: (res: { labels: string[]; rows: Array<Record<string, unknown>> }): BrandSeries => {
      const byBrand: Record<string, Array<number | null>> = {};
      for (const b of brands) byBrand[b] = res.labels.map(() => null);
      for (const r of res.rows) {
        const b = str(r['brand']);
        const i = num(r['_i']);
        const target = byBrand[b];
        if (target && i != null) target[i] = num(r[field]);
      }
      return { labels: res.labels, byBrand };
    },
    queryFn: async ({ signal }) => {
      const res = await postOverlay<Record<string, unknown>>(
        '/v1/availability/competition/series',
        { ...scopeBody({ customer_name: customerName, category, city }, range), brands },
        signal,
      );
      const days = [...new Set(res.rows.map((r) => str(r['day'])))].sort();
      const at = new Map(days.map((d, i) => [d, i]));
      return {
        labels: days.map(dayLabel),
        rows: res.rows.map((r) => ({ ...r, _i: at.get(str(r['day'])) ?? null })),
      };
    },
  });
}

/** Stable, high-contrast line colours; the tenant's own brand always leads. */
export const BRAND_COLORS = [
  '#2f6feb', '#f5a524', '#12a594', '#e0489d', '#7c5cff',
  '#e5484d', '#0891b2', '#8b8fa3', '#16a34a', '#d97706',
];
